import { useState } from 'react'
import { toast } from 'react-toastify'
import { useMutation, useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Typography } from '@mui/material'
import { Button, LoginInput } from 'components'
import { ResetPasswordValidationSchema } from 'utils'
import EmailIcon from 'assets/svgComponents/EmailIcon'
import KeyIcon from 'assets/svgComponents/KeyIcon'
import useUserService from 'services/userService'
import { successToast } from 'utils/toastService'
import SignInLogo from 'assets/svgComponents/SignInLogo'
import PasswordRequirements from './PasswordRequirements'
import { StyledForm, TextsContainer } from './ResetPasswordPage.styled'

export const ResetPasswordPage = () => {
  const { token } = useParams()
  const { resetPassword, validateToken } = useUserService()
  const navigate = useNavigate()
  const [passwordTouched, setPasswordTouched] = useState(false)

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(ResetPasswordValidationSchema),
    mode: 'onChange',
  })

  const passwordValue = watch('password') || ''
  const confirmPasswordValue = watch('confirmPassword') || ''

  const {
    data: tokenValid,
    isLoading,
    isError,
  } = useQuery(['validate-reset-token', token], () => validateToken(token), {
    enabled: !!token,
    retry: false,
  })

  const { mutate: resetPasswordMutate, isLoading: isResetting } = useMutation(
    ({ password, token }: { password: string; token: string }) =>
      resetPassword(password, token),
    {
      onSuccess: () => {
        successToast('Password has been updated')
        navigate('/auth/login')
      },
      onError: (error: any) => {
        const msg = error?.detail || 'Failed to reset password'
        toast.error(msg)
      },
    }
  )

  const onSubmit = ({ password }) => {
    if (token) {
      resetPasswordMutate({ password, token })
    } else {
      toast.error('Token missing from URL')
    }
  }

  if (isLoading) {
    return <Typography>Loading...</Typography>
  }

  if (isError || !tokenValid?.valid) {
    return <Typography color="error">Invalid or expired token.</Typography>
  }

  return (
    <Box
      width="400px" // Increased width to better accommodate requirements
      display="flex"
      flexDirection="column"
      justifyContent="center"
      gap="40px"
    >
      <Box display="flex" gap="10px" justifyContent="center">
        <SignInLogo />
        <Box display="flex" justifyContent="center" flexDirection="column">
          <Typography
            component="h1"
            lineHeight="20px"
            fontWeight="800"
            color="#C93C41"
            fontSize="26px"
          >
            EQUINOM
          </Typography>
          <TextsContainer>
            <Typography>Manna</Typography>
            <Typography fontWeight="700">Wheat</Typography>
          </TextsContainer>
        </Box>
      </Box>

      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <Typography>Please enter your new password</Typography>
        <Box display="flex" flexDirection="column">
          <Controller
            name="password"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <LoginInput
                placeholder="Password"
                type="password"
                error={!!errors['password']}
                preIcon={<EmailIcon />}
                cut="top"
                {...field}
                onBlur={() => {
                  field.onBlur()
                  setPasswordTouched(true)
                }}
                onChange={(e) => {
                  field.onChange(e)
                  if (!passwordTouched && e.target.value) {
                    setPasswordTouched(true)
                  }
                }}
              />
            )}
          />
          <Controller
            name="confirmPassword"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <LoginInput
                placeholder="Confirm Password"
                type="password"
                error={!!errors['confirmPassword']}
                cut="bottom"
                preIcon={<KeyIcon />}
                {...field}
              />
            )}
          />
          <PasswordRequirements
            password={passwordValue}
            newPassword={confirmPasswordValue}
            touched={passwordTouched}
          />
        </Box>

        <Box display="flex" justifyContent="center" alignItems="center">
          <Button
            type="submit"
            width="150px"
            disabled={!isValid || isResetting}
          >
            {isResetting ? 'Submitting...' : 'Submit'}
          </Button>
        </Box>
      </StyledForm>
    </Box>
  )
}
